import { useState } from "react";
import { Lock, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import { BRAND, BOOKING } from "@/lib/site";

const Footer = () => {
  const [contactOpen, setContactOpen] = useState(false);

  const explore = [
    { label: "Safety information", href: "#safety", testid: "footer-safety" },
    { label: "How it works", href: "#how-it-works", testid: "footer-how-it-works" },
    { label: "Location", href: "#location", testid: "footer-location" },
  ];

  return (
    <footer data-testid="site-footer" className="border-t border-line/60 bg-ink">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 md:py-20 grid gap-12 md:grid-cols-12">
        <div className="md:col-span-5">
          <img
            src="/assets/logo.png"
            alt="TEMPA health logo"
            data-testid="footer-logo"
            className="w-44 sm:w-56 -ml-3 -mt-2"
          />
          <p className="mt-2 max-w-sm text-sm font-light leading-relaxed text-stone2">
            Private sauna and ice-bath recovery in {BRAND.city}. Operating at{" "}
            {BRAND.parent}, {BRAND.address}.
          </p>
        </div>

        <nav className="md:col-span-3" aria-label="Book">
          <p className="text-xs tracking-[0.25em] uppercase font-medium text-stone2">Book</p>
          <ul className="mt-5 space-y-3">
            <li>
              <a href={BOOKING.book} data-testid="footer-booking" className="text-sm font-light text-sand hover:text-cream transition-colors duration-300">
                Booking
              </a>
            </li>
            <li>
              <a href={BOOKING.pedalPop} data-testid="footer-pedal-pop" className="text-sm font-light text-sand hover:text-cream transition-colors duration-300">
                Recovery Pop bookings
              </a>
            </li>
            <li>
              <a href={BOOKING.memberships} data-testid="footer-membership" className="text-sm font-light text-sand hover:text-cream transition-colors duration-300">
                Join membership
              </a>
            </li>
            <li>
              <a href={BOOKING.account} data-testid="footer-member-login" className="text-sm font-light text-sand hover:text-cream transition-colors duration-300">
                Member login / My bookings
              </a>
            </li>
            <li>
              <a href="#pricing" data-testid="footer-pricing" className="text-sm font-light text-sand hover:text-cream transition-colors duration-300">
                Pricing
              </a>
            </li>
          </ul>
        </nav>

        <nav className="md:col-span-4" aria-label="Explore">
          <p className="text-xs tracking-[0.25em] uppercase font-medium text-stone2">Explore</p>
          <ul className="mt-5 space-y-3">
            {explore.map((link) => (
              <li key={link.label}>
                <a href={link.href} data-testid={link.testid} className="text-sm font-light text-sand hover:text-cream transition-colors duration-300">
                  {link.label}
                </a>
              </li>
            ))}
            <li>
              <button
                type="button"
                onClick={() => {
                  setContactOpen((v) => !v);
                  if (!contactOpen) toast.info("The Pedal Pop team manages all administration for TEMPA.");
                }}
                data-testid="footer-contact"
                className="text-sm font-light text-sand hover:text-cream transition-colors duration-300"
              >
                Contact
              </button>
              {contactOpen && (
                <div data-testid="footer-contact-options" className="mt-3 space-y-2 border-l border-line pl-4">
                  <a
                    href="tel:0414253311"
                    data-testid="footer-contact-call"
                    className="flex items-center gap-2.5 text-sm text-cream hover:text-ember transition-colors duration-300"
                  >
                    <Phone className="h-3.5 w-3.5 text-ember" aria-hidden="true" />
                    Call 0414 253 311
                  </a>
                  <a
                    href="mailto:team@pedalpop.com.au"
                    data-testid="footer-contact-email"
                    className="flex items-center gap-2.5 text-sm text-cream hover:text-ember transition-colors duration-300"
                  >
                    <Mail className="h-3.5 w-3.5 text-ember" aria-hidden="true" />
                    team@pedalpop.com.au
                  </a>
                  <p className="text-xs font-light leading-relaxed text-stone2 pt-1">
                    The {BRAND.parent} team manages all administration for {BRAND.parts[0].toUpperCase()}.
                  </p>
                </div>
              )}
            </li>
          </ul>
        </nav>
      </div>

      <div className="border-t border-line/60">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <p className="text-xs text-stone2">© 2026 {BRAND.name}. All rights reserved.</p>
          <div className="flex items-center gap-5">
            <a
              href="/staff"
              data-testid="footer-staff-link"
              className="inline-flex items-center gap-1.5 min-h-[44px] text-xs text-stone2 hover:text-cream transition-colors duration-300"
            >
              <Lock className="h-3 w-3" aria-hidden="true" />
              Staff
            </a>
            <p className="text-xs text-stone2">{BRAND.city}, Western Australia</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
